<template>
  <!-- Minimized: a pill that says the assistant is there and whether it has something unsaid. -->
  <button v-if="state === PANEL_MINIMIZED" class="agent-panel__pill d-flex align-items-center justify-content-center"
    type="button" :aria-label="unread ? `Open ${agentName}, new message` : `Open ${agentName}`"
    data-cy="agent-panel-pill" @click="setState(PANEL_EXPANDED)">
    <font-awesome-icon :icon="faSparkles" />
    <span v-if="unread" class="agent-panel__unread" data-cy="agent-panel-unread" />
  </button>

  <!-- Collapsed: still reachable, still typeable, w/out covering the page. -->
  <div v-else-if="state === PANEL_COLLAPSED" class="agent-panel__bar" data-cy="agent-panel-collapsed">
    <div v-if="completed" class="d-flex align-items-center gap-2 ps-2">
      <p class="agent-panel__completed flex-grow-1 mb-0 py-1" data-cy="agent-panel-completed">
        This conversation has wrapped up — keep an eye on your dashboard and email for updates.
      </p>
      <button class="agent-panel__icon-btn d-flex align-items-center justify-content-center flex-shrink-0"
        type="button" aria-label="Open the assistant" data-cy="agent-panel-bar-expand"
        @click="setState(PANEL_EXPANDED)">
        <font-awesome-icon :icon="faUpRightAndDownLeftFromCenter" />
      </button>
    </div>
    <AgentComposer v-else v-model="draft" :placeholder="barPlaceholder" :disabled="disabled" show-expand @send="onSend"
      @expand="setState(PANEL_EXPANDED)" />
  </div>

  <!-- Expanded -->
  <section v-else class="agent-panel" :aria-label="agentName" data-cy="agent-panel-expanded"
    @keydown.esc="setState(PANEL_COLLAPSED)">
    <header class="agent-panel__header d-flex align-items-center px-4 py-3">
      <span class="agent-panel__avatar d-flex align-items-center justify-content-center me-2 flex-shrink-0">
        <font-awesome-icon :icon="faSparkles" />
      </span>
      <span class="agent-panel__title flex-grow-1 fw-bold">{{ agentName }}</span>
      <button class="agent-panel__icon-btn d-flex align-items-center justify-content-center flex-shrink-0" type="button"
        aria-label="Collapse the assistant" data-cy="agent-panel-collapse" @click="setState(PANEL_COLLAPSED)">
        <font-awesome-icon :icon="faDownLeftAndUpRightToCenter" />
      </button>
    </header>

    <p class="agent-panel__disclaimer text-center px-4 pb-3 mb-0">
      AI can make mistakes. Please check responses. See our
      <a :href="`${hpUrl}/agreements/privacy-policy/`" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      for how data is processed and stored.
    </p>

    <!-- aria-live: the panel opens itself, so a borrower may not be looking when a turn arrives -->
    <div ref="messagesEl" class="agent-panel__messages px-4" role="log" aria-live="polite"
      data-cy="agent-panel-messages">
      <p v-if="invitation" class="agent-panel__message agent-panel__message--agent" data-cy="agent-panel-invitation">
        {{ invitation }}
      </p>

      <template v-for="(message, index) in messages" :key="index">
        <p v-if="message.role === 'user'" class="agent-panel__message agent-panel__message--user">
          {{ message.text }}
        </p>
        <!-- eslint-disable-next-line vue/no-v-html -- sanitized to tel: anchors only, see renderAgentMessage -->
        <p v-else class="agent-panel__message agent-panel__message--agent" v-html="renderAgentMessage(message.text)" />
      </template>

      <p v-if="responding" class="agent-panel__message agent-panel__message--agent agent-panel__typing"
        data-cy="agent-panel-typing">
        <span class="agent-panel__dot" />
        <span class="agent-panel__dot" />
        <span class="agent-panel__dot" />
      </p>
    </div>

    <!-- Canned prompts are v2; the row renders nothing until suggestions are supplied. -->
    <div v-if="suggestions.length" class="agent-panel__suggestions px-3 pb-2 d-flex flex-wrap gap-2"
      data-cy="agent-panel-suggestions">
      <button v-for="suggestion in suggestions" :key="suggestion" class="agent-panel__suggestion" type="button"
        @click="onSend(suggestion)">
        {{ suggestion }}
      </button>
    </div>

    <div class="agent-panel__footer px-4 py-3">
      <p v-if="completed" class="agent-panel__completed text-center mb-0" data-cy="agent-panel-completed">
        This conversation has wrapped up — keep an eye on your dashboard and email for updates.
      </p>
      <AgentComposer v-else ref="composerEl" v-model="draft" stacked :disabled="disabled" @send="onSend" />
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import {
  faSparkles,
  faDownLeftAndUpRightToCenter,
  faUpRightAndDownLeftFromCenter,
} from '@fortawesome/pro-regular-svg-icons'
import env from '~/libs/env'
import AgentComposer from '~/components/portal/ai/AgentComposer.vue'
import { renderAgentMessage } from '~/libs/agent-message'
import { PANEL_EXPANDED, PANEL_COLLAPSED, PANEL_MINIMIZED } from '~/composables/useBorrowerAgent'

/**
 * The borrower-facing chat surface, shared by every agent.
 * Presentational only — takes messages, emits sends; whoever mounts it owns the conversation.
 */
const props = defineProps({
  agentName: {
    type: String,
    default: 'Lendio Assistant',
  },
  /**
   * One of PANEL_EXPANDED | PANEL_COLLAPSED | PANEL_MINIMIZED.
   */
  state: {
    type: String,
    default: PANEL_EXPANDED,
  },
  /**
   * `{ role: 'agent' | 'user', text: string }`
   */
  messages: {
    type: Array,
    default: () => [],
  },
  /**
   * The opening message, rendered client-side w/out calling the model.
   */
  invitation: {
    type: String,
    default: '',
  },
  responding: {
    type: Boolean,
    default: false,
  },
  unread: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * The interview has ended — the composer is retired in favor of a completion note.
   */
  completed: {
    type: Boolean,
    default: false,
  },
  /**
   * Canned prompts — v2. Empty renders no chip row.
   */
  suggestions: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:state', 'send'])

/**
 * marketing-site policy page, same as AppFooter — the portal origin has no such route
 */
const hpUrl = env('hpUrl')

const messagesEl = ref(null)
const composerEl = ref(null)

/**
 * lives here, not in the composer — state changes swap composer instances
 */
const draft = ref('')

// mid-interview the bar's job is pulling the borrower back, not open-ended asking
const barPlaceholder = computed(() =>
  props.messages.length ? 'Continue the conversation…' : 'Ask your question…'
)

function setState(next) {
  emit('update:state', next)
}

function onSend(text) {
  emit('send', text)
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesEl.value) {
      messagesEl.value.scrollTop = messagesEl.value.scrollHeight
    }
  })
}

watch(() => props.messages, scrollToBottom, { deep: true })

// input is disabled while the agent responds — restore focus once it re-enables
watch(
  () => props.responding,
  (responding, wasResponding) => {
    scrollToBottom()

    if (wasResponding && !responding && props.state === PANEL_EXPANDED) {
      nextTick(() => composerEl.value?.focus())
    }
  }
)

watch(
  () => props.state,
  (state) => {
    if (state === PANEL_EXPANDED) {
      // the transcript remounts at scrollTop 0 — jump to the latest turn
      scrollToBottom()
      nextTick(() => composerEl.value?.focus())
    }
  }
)
</script>

<style scoped lang="scss">
// Lendio styleguide values — not auto-injected into SFC style blocks.
$border-gray-100: #E5E5E5;
$type-300: #6B717A;
$type-200: #858D94;
$background-50: #F6F6F6;
$brand-yellow: #E4FB65;

.agent-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgb(0 0 0 / 14%);
  overflow: hidden;

  &__header {
    border-bottom: 1px solid $border-gray-100;
  }

  &__title {
    font-size: 1.0625rem;
  }

  &__avatar {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background: $brand-yellow;
  }

  &__icon-btn {
    width: 1.75rem;
    height: 1.75rem;
    border: none;
    background: transparent;
    color: $type-300;
  }

  &__disclaimer {
    font-size: 0.75rem;
    color: $type-200;
    border-bottom: 1px solid $border-gray-100;
    padding-top: 0.75rem;
  }

  &__messages {
    flex: 1 1 auto;
    overflow-y: auto;
    padding-top: 1.25rem;
  }

  &__message {
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 1.25rem;

    // Agent turns read as prose; borrower turns as bubbles — keeps longer explanations legible.
    &--agent {
      color: inherit;
      // pre-line honors the model's \n paragraph breaks; sanitized output stays intact
      white-space: pre-line;
    }

    &--user {
      margin-left: auto;
      width: fit-content;
      max-width: 85%;
      padding: 0.625rem 1rem;
      border-radius: 16px;
      background: $background-50;
      // the composer allows Shift+Enter newlines; keep them
      white-space: pre-line;
    }
  }

  &__typing {
    display: flex;
    gap: 0.25rem;
  }

  &__dot {
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    background: $type-200;
    animation: agent-panel-blink 1.4s infinite both;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  &__suggestion {
    border: none;
    border-radius: 999px;
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
    background: $type-300;
    color: #fff;
  }

  &__footer {
    // no divider — the stacked composer carries its own border, per the design
    padding-top: 0.25rem;
  }

  &__completed {
    font-size: 0.875rem;
    color: $type-300;
  }

  &__bar {
    background: #fff;
    border: 1px solid $border-gray-100;
    border-radius: 999px;
    box-shadow: 0 2px 12px rgb(0 0 0 / 12%);
    padding: 0.375rem 0.5rem 0.375rem 0.75rem;
  }

  &__pill {
    position: relative;
    width: 3.25rem;
    height: 2rem;
    border: none;
    border-radius: 999px;
    background: $brand-yellow;
    box-shadow: 0 2px 12px rgb(0 0 0 / 12%);
  }

  &__unread {
    position: absolute;
    top: -0.125rem;
    right: -0.125rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #e5484d;
  }
}

@keyframes agent-panel-blink {

  0%,
  80%,
  100% {
    opacity: 0.2;
  }

  40% {
    opacity: 1;
  }
}
</style>
